import type { MessageData, PaginatedResponse, SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { organizationEndpoints, replaceParams } from "@/app/api/endpoints";
import type {
	CreateOrganizationRequest,
	ListOrganizationsParams,
	Organization,
	OrganizationTreeItem,
	OrganizationTreeQuery,
	OrgPickerTreeNode,
	UpdateOrganizationRequest,
} from "../types";

export const organizationService = {
	/** RFC-002.1: Get organization hierarchy tree */
	async getTree(params?: OrganizationTreeQuery): Promise<OrganizationTreeItem[]> {
		const response = await apiClient.get<{ success: true; data: OrganizationTreeItem[] }>(
			organizationEndpoints.tree,
			{ params }
		);
		return response.data.data;
	},

	/** Lean org hierarchy for search-picker components (id/name/parentId only). */
	async getPickerTree(): Promise<OrgPickerTreeNode[]> {
		const response = await apiClient.get<{ success: true; data: OrgPickerTreeNode[] }>(
			organizationEndpoints.treePicker
		);
		return response.data.data;
	},

	async list(params: ListOrganizationsParams): Promise<PaginatedResponse<Organization>> {
		const response = await apiClient.get<PaginatedResponse<Organization>>(
			organizationEndpoints.list,
			{ params }
		);
		return response.data;
	},

	async getById(id: string): Promise<Organization> {
		const url = replaceParams(organizationEndpoints.getById, { id });
		const response = await apiClient.get<SuccessResponse<Organization>>(url);
		return response.data.data;
	},

	async listChildren(
		id: string,
		params?: { page?: number; limit?: number }
	): Promise<PaginatedResponse<Organization>> {
		const url = replaceParams(organizationEndpoints.children, { id });
		const response = await apiClient.get<PaginatedResponse<Organization>>(url, { params });
		return response.data;
	},

	async listMyChildren(params?: {
		page?: number;
		limit?: number;
	}): Promise<PaginatedResponse<Organization>> {
		const response = await apiClient.get<PaginatedResponse<Organization>>(
			organizationEndpoints.myChildren,
			{ params }
		);
		return response.data;
	},

	async listMyCrossAccessOrganizations(params?: {
		page?: number;
		limit?: number;
	}): Promise<PaginatedResponse<Organization>> {
		const response = await apiClient.get<PaginatedResponse<Organization>>(
			organizationEndpoints.myChildrenCrossAccess,
			{ params }
		);
		return response.data;
	},

	async create(data: CreateOrganizationRequest): Promise<Organization> {
		const response = await apiClient.post<SuccessResponse<Organization>>(
			organizationEndpoints.create,
			data
		);
		return response.data.data;
	},

	async update(id: string, data: UpdateOrganizationRequest): Promise<Organization> {
		const url = replaceParams(organizationEndpoints.update, { id });
		const response = await apiClient.patch<SuccessResponse<Organization>>(url, data);
		return response.data.data;
	},

	async delete(id: string): Promise<void> {
		const url = replaceParams(organizationEndpoints.delete, { id });
		await apiClient.delete<SuccessResponse<MessageData>>(url);
	},
};
